import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { texts } from 'utils/languages/fr';
import { useGetShareCategoriesQuery } from 'api/main';
import { CategoryProvider, useCategoryContext } from './CategoryContext';
import CategoryItem from './CategoryItem';
import CategoryForm from './CategoryForm';

import { ActionButton, Loader } from 'components';
import { Box, Collapse } from '@mui/material';

const FundShareCategoriesContent = ({ fundId }) => {
    const { setNewCategory, setEditingCategoryId, editingCategoryId, newCategory } =
        useCategoryContext();

    const { data: categories = [], isLoading } = useGetShareCategoriesQuery({
        investmentFund: fundId,
    });

    const handleNewCategory = useCallback(() => {
        if (editingCategoryId || newCategory) {
            // If there's already an editing state, we might want to handle this case
            // For now, we'll just reset and start new
            setEditingCategoryId(null);
            setNewCategory(null);
        }
        setNewCategory({});
    }, [editingCategoryId, newCategory, setEditingCategoryId, setNewCategory]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
            gap={2}
            paddingY={3}
        >
            <Loader loading={isLoading}>
                {categories.map(category => (
                    <CategoryItem key={category.id} category={category} fundId={fundId} />
                ))}
                <Collapse
                    in={!!newCategory}
                    sx={theme => ({
                        border: `3px solid ${theme.palette.primary.main}`,
                        borderRadius: '5px',
                        padding: theme.spacing(2, 3),
                        width: '420px',
                    })}
                >
                    {newCategory && (
                        <CategoryForm
                            category={null}
                            fundId={fundId}
                            onCancel={() => {
                                setNewCategory(null);
                            }}
                        />
                    )}
                </Collapse>
                {!newCategory && (
                    <ActionButton onClick={handleNewCategory} variant="contained">
                        {texts.ADD_CATEGORY}
                    </ActionButton>
                )}
            </Loader>
        </Box>
    );
};

FundShareCategoriesContent.propTypes = {
    fundId: PropTypes.number.isRequired,
};

const FundShareCategories = ({ fundId }) => {
    return (
        <CategoryProvider>
            <FundShareCategoriesContent fundId={fundId} />
        </CategoryProvider>
    );
};

FundShareCategories.propTypes = {
    fundId: PropTypes.number.isRequired,
};

export default FundShareCategories;
